import { inject, Injectable } from '@angular/core';
import { ClientPackage, ClientPackageDto } from '../models/client-package.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { ClientService } from './client.service';
import { DialogService } from '../../shared/services/dialog.service';
import { CourseService } from './course.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiService } from '../../shared/services/api.service';
import { FormField } from '../models/form/form-field.model';
import { CLIENT_PACKAGE_FORM } from '../models/form/client-package-form';
import { Client, FilterFieldMap, PayloadRequest, PayloadResponse } from '../models';
import { AuthService } from '../../auth/services/auth.service';
import { trimFormValues } from '../../shared/utils';
import { DEFAULT_END_DATE, DEFAULT_START_DATE } from '../constants/date.constants';

@Injectable({
  providedIn: 'root'
})
export class ClientPackageService {
  private apiService = inject(ApiService);
  private dialogService = inject(DialogService);
  private clientService = inject(ClientService);
  constructor(
    private fb: FormBuilder,
    private router: Router,
  ) { }



  createClientPackageForm(): FormGroup {
    const formBuilder = new FormBuilder();
    const formControls: { [key: string]: FormControl } = {};

    Object.keys(CLIENT_PACKAGE_FORM).forEach(key => {
      const field = CLIENT_PACKAGE_FORM[key];
      formControls[key] = new FormControl(
        { value: field.value, disabled: !!field.disabled },
        field.validators
      );
    });

    return formBuilder.group(formControls);
  }

  getClientPackageFormField(): Observable<any> {
    return of(CLIENT_PACKAGE_FORM);
  }

  navigateToList(): void {
    this.router.navigate(['/main/licenzeClienti']);
  }

  getClientPackageList(filter: FilterFieldMap,
    pageNumber?: number,
    pageSize?: number
  ): Observable<PayloadResponse<ClientPackage[]>> {
    try {
      let params = this.setParams(filter);

      if (pageNumber) params = params.set('pageNumber', pageNumber);
      if (pageSize) params = params.set('pageSize', pageSize);

      return this.apiService.get('clients-package', { params });
    } catch {
      this.showInvalidDateRangeDialog();
      return of();
    }
  }

  downloadClientPackagesCsv(filter: FilterFieldMap): Observable<any> {
    try {
      const params = this.setParams(filter);

      return this.apiService.get('clients-package/client-csv', {
        params,
        responseType: 'blob',
        observe: 'response'
      });
    } catch {
      this.showInvalidDateRangeDialog();
      return of();
    }
  }

  getAllClientsFromClientPackageList(resellerId: number): Observable<PayloadResponse<{ id: number; name: string}[]>> {
    return this.apiService.get(`clients-package/client-list/${resellerId}`);
  }

  getClientPackageById(cliPackId: number): Observable<PayloadResponse<ClientPackage>> {
    return this.apiService.get(`clients-package/${cliPackId}`).pipe(
      map(cliPack => {
        return {
          ...cliPack.data,
          resellerPackageId: cliPack.data.resellerPackageId.resellerPackageId,
          clientId: cliPack.data.clientId.clientId,
        }
      })
    );
  }

  onSubmitForm(formClientPackage: FormGroup): Observable<PayloadResponse<ClientPackage>> {

    trimFormValues(formClientPackage);

    if (formClientPackage.invalid) {

      formClientPackage.markAllAsTouched();

      this.dialogService.openDialogInfoNoRedirect('Errore', 'Compila tutti i campi obbligatori.');

      return of();
    }

    const cliPack = this.convertTypeForm(formClientPackage);
    return this.saveClientPackage(cliPack).pipe(
      tap(() => this.showDialogSuccess())
    );;

  }
  private saveClientPackage(clientPackage: ClientPackageDto): Observable<PayloadResponse<ClientPackage>> {
    const payload = new PayloadRequest<ClientPackageDto>({ data: clientPackage });
    return this.apiService.post('clients-package/create', payload);
  }

  private showDialogSuccess(): void {
    this.dialogService.openDialogAlert('Operazione completata', 'Hai assegnato le licenze al rivenditore con successo, é stata inviata una email con i codici delle licenze da assegnare', () => {
      this.router.navigate(['/main/licenzeClienti']);
    });
  }

  convertTypeForm(form: FormGroup): ClientPackageDto {
    const cliPack: ClientPackageDto = {
      clientPackageId: form.value.clientPackageId,
      resellerPackageId: Number(form.value.resellerPackageId),
      clientId: Number(form.value.clientId),
      soldLicenses: form.value.soldLicenses,
      orderType: form.value.orderType,
    }
    return cliPack;
  }

  private setParams(filter: FilterFieldMap): HttpParams {
    let params = new HttpParams();
    if (filter) {
      if (filter['reseller'].value) params = params.set('resellerId', filter['reseller'].value);
      if (filter['client'].value) params = params.set('clientId', filter['client'].value);
      if (filter['course'].value) params = params.set('courseTitle', filter['course'].value);
    }

    const startDate = filter['startDate']?.value || DEFAULT_START_DATE;
    const endDate = filter['endDate']?.value || DEFAULT_END_DATE;

    if (startDate > endDate) {
      throw new Error('Invalid date range');
    }

    return params
      .set('startDate', startDate)
      .set('endDate', endDate);
  }

  private showInvalidDateRangeDialog(): void {
    this.dialogService.openDialogError(
      'Errore',
      'La data di fine deve essere uguale o successiva alla data di inizio.'
    );
  }
}
